import React, { useEffect, useState } from "react";
import {
  createFileChargeRule,
  deleteFileChargeRule,
  getFileChargeRules,
  updateFileChargeRule,
} from "../../../apis/fileChargeRules";

const months = Array.from({ length: 24 }, (_, index) => index + 1);
const emptyMonthlyCharges = () => Object.fromEntries(months.map((month) => [month, ""]));
const emptyForm = () => ({
  start_amount: "",
  end_amount: "",
  monthly_charges: emptyMonthlyCharges(),
  description: "",
});
const money = (value: unknown) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

export default function AutoFileChargeRulesPage() {
  const [rules, setRules] = useState<any[]>([]);
  const [form, setForm] = useState<any>(emptyForm());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState("");

  const loadRules = async () => {
    try {
      setRules((await getFileChargeRules())?.data || []);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || "Unable to load rules");
    }
  };

  useEffect(() => { loadRules(); }, []);

  const saveRule = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    const monthlyCharges = Object.fromEntries(
      months.map((month) => [month, Number(form.monthly_charges[month])])
    );
    const payload = {
      start_amount: Number(form.start_amount),
      end_amount: Number(form.end_amount),
      charge_type: "fixed",
      charge_amount: monthlyCharges[1],
      monthly_charges: monthlyCharges,
      description: form.description,
      enabled: true,
    };
    try {
      if (editingId) await updateFileChargeRule(editingId, payload);
      else await createFileChargeRule(payload);
      setForm(emptyForm());
      setEditingId(null);
      loadRules();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || "Unable to save rule");
    }
  };

  const editRule = (rule: any) => {
    const savedCharges = typeof rule.monthly_charges === "string"
      ? JSON.parse(rule.monthly_charges || "{}")
      : rule.monthly_charges || {};
    const legacyCharges: Record<number, unknown> = {
      3: rule.emi_3_month_charge,
      5: rule.emi_5_month_charge,
      6: rule.emi_6_month_charge,
    };
    setForm({
      start_amount: String(rule.start_amount),
      end_amount: String(rule.end_amount),
      monthly_charges: Object.fromEntries(months.map((month) => [
        month,
        String(savedCharges[month] ?? legacyCharges[month] ?? rule.charge_amount ?? ""),
      ])),
      description: rule.description || "",
    });
    setEditingId(rule.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleRule = async (rule: any) => {
    try {
      await updateFileChargeRule(rule.id, { enabled: !rule.enabled });
      loadRules();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || "Unable to update rule");
    }
  };

  const deleteRule = async (id: number) => {
    if (!window.confirm("Delete this file charge rule?")) return;
    try {
      await deleteFileChargeRule(id);
      loadRules();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || "Unable to delete rule");
    }
  };

  const getMonthlyCharge = (rule: any, month: number) => {
    const saved = typeof rule.monthly_charges === "string"
      ? JSON.parse(rule.monthly_charges || "{}")
      : rule.monthly_charges || {};
    return saved[month] ?? ({ 3: rule.emi_3_month_charge, 5: rule.emi_5_month_charge, 6: rule.emi_6_month_charge } as any)[month] ?? rule.charge_amount;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="card">
          <div className="card-header bg-primary text-white">
            <h4 className="mb-1">Auto File Charge Rules</h4>
            <small>Create an amount slab and enter a separate file charge for every EMI tenure from 1 to 24 months.</small>
          </div>
          <div className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={saveRule}>
              <div className="row g-3 mb-4">
                <div className="col-md-3"><label className="form-label">Slab From</label><input type="number" min="0" className="form-control" value={form.start_amount} onChange={(event) => setForm({ ...form, start_amount: event.target.value })} required /></div>
                <div className="col-md-3"><label className="form-label">Slab To</label><input type="number" min="0" className="form-control" value={form.end_amount} onChange={(event) => setForm({ ...form, end_amount: event.target.value })} required /></div>
                <div className="col-md-6"><label className="form-label">Description</label><input className="form-control" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></div>
              </div>
              <h6 className="mb-3">Monthly File Charges</h6>
              <div className="row g-3">
                {months.map((month) => <div className="col-6 col-md-3 col-lg-2" key={month}><label className="form-label">{month} Month{month > 1 ? "s" : ""}</label><div className="input-group"><span className="input-group-text">Rs.</span><input type="number" min="0" className="form-control" value={form.monthly_charges[month]} onChange={(event) => setForm({ ...form, monthly_charges: { ...form.monthly_charges, [month]: event.target.value } })} required /></div></div>)}
              </div>
              <div className="d-flex gap-2 mt-4"><button className="btn btn-primary" type="submit">{editingId ? "Update Rule" : "Add Rule"}</button>{editingId && <button type="button" className="btn btn-light" onClick={() => { setEditingId(null); setForm(emptyForm()); }}>Cancel</button>}</div>
            </form>
          </div>
        </div>

        <div className="card"><div className="card-body"><div className="table-responsive"><table className="table table-bordered table-sm align-middle mb-0"><thead><tr><th>Slab From</th><th>Slab To</th>{months.map((month) => <th key={month}>{month}M</th>)}<th>Status</th><th>Actions</th></tr></thead><tbody>{rules.length === 0 ? <tr><td colSpan={28} className="text-center text-muted py-4">No rules configured. Manual file charge is active.</td></tr> : rules.map((rule) => <tr key={rule.id}><td>{money(rule.start_amount)}</td><td>{money(rule.end_amount)}</td>{months.map((month) => <td key={month}>{money(getMonthlyCharge(rule, month))}</td>)}<td>{rule.enabled ? "Active" : "Inactive"}</td><td className="text-nowrap"><button className="btn btn-sm btn-outline-primary me-1" onClick={() => editRule(rule)}>Edit</button><button className="btn btn-sm btn-outline-warning me-1" onClick={() => toggleRule(rule)}>{rule.enabled ? "Deactivate" : "Activate"}</button><button className="btn btn-sm btn-outline-danger" onClick={() => deleteRule(rule.id)}>Delete</button></td></tr>)}</tbody></table></div></div></div>
      </div>
    </div>
  );
}
